import { useNow } from '@/composables/game/time'
import { computed } from 'vue'
import { formatDuration } from '@/helpers/date'
import { useConfig } from '@/composables/game/config'
import {
  type Challenge,
  CHALLENGE_PRIZE_TYPE,
  isPrizePoolCash,
  isPrizePoolNft,
  isPrizePoolSpecialCurrency,
  type PrizePoolAmounts,
  REWARD_LIVE_OP_CHALLENGE_TYPE,
} from '@/types/shared/player-config'
import { SPECIAL_CURRENCY_ID } from '@/types/shared/player'
import TonSymbolImage from '@/assets/images/ton-symbol.png'
import DropeeTokenImage from '@/assets/images/dropee-token-new.webp'
import NftImage from '@/assets/images/icons/telegram-icon-blue.webp'
import BadgeImage from '@/assets/images/icons/badge-icon.webp'
import UsdtTokenImage from '@/assets/images/usdt.webp'
import { availableBadges } from '@/composables/game/badge'
import { ChallengeMedal } from '@/types/leaderboard-player'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import { formatNumberFullTruncate } from '@/helpers/utils'

function getNormalizedPrizePool(pool: { type: CHALLENGE_PRIZE_TYPE, amounts: PrizePoolAmounts }) {
  const normalizedAmounts: PrizePoolAmounts = []

  for (const prizePool of pool.amounts) {
    if (isPrizePoolCash(prizePool)) {
      const fromRank = prizePool.fromRank
      const toRank = prizePool.toRank
      const amount = prizePool.amount
      const badgeId = prizePool.badgeId
      // separate always the first 3 places even if they would have the same prize:
      if (fromRank <= 3) {
        for (let i = fromRank; i <= Math.min(toRank, 3); i++) {
          normalizedAmounts.push({ fromRank: i, toRank: i, amount, badgeId })
        }

        if (toRank > 3) {
          normalizedAmounts.push({ fromRank: 4, toRank, amount, badgeId })
        }
      } else {
        normalizedAmounts.push({ fromRank, toRank, amount, badgeId })
      }
    } else {
      normalizedAmounts.push(prizePool)
    }
  }

  return {
    ...pool,
    amounts: normalizedAmounts,
  }
}

function calculateTotalPrize(pool: { type: CHALLENGE_PRIZE_TYPE, amounts: PrizePoolAmounts } | undefined | null) {
  return pool?.amounts.reduce((sum, prize) => {
    if (isPrizePoolCash(prize) || isPrizePoolSpecialCurrency(prize)) {
      return sum + prize.amount * (prize.toRank + 1 - prize.fromRank)
    }
    return sum
  }, 0)
}

export function getPrizeTypeImage(prizeType: CHALLENGE_PRIZE_TYPE | undefined, specialCurrencyId?: string): string {
  if (!prizeType) return ''
  if (prizeType === CHALLENGE_PRIZE_TYPE.TON) return TonSymbolImage
  if (prizeType === CHALLENGE_PRIZE_TYPE.USDT) return UsdtTokenImage
  if (prizeType === CHALLENGE_PRIZE_TYPE.DROPEE) return DropeeTokenImage
  if (prizeType === CHALLENGE_PRIZE_TYPE.BADGE) return BadgeImage
  if (prizeType === CHALLENGE_PRIZE_TYPE.SPECIAL_CURRENCY && specialCurrencyId) {
    return allSpecialCurrenciesConfig.value[specialCurrencyId]?.image || ''
  }
  return ''
}

function getPrize(position: number, pool?: { type: CHALLENGE_PRIZE_TYPE, amounts: PrizePoolAmounts } | null) {
  if (!pool) return
  return pool.amounts.find(({ fromRank, toRank }) => position >= fromRank && position <= toRank)
}

function getBadge(position: number, pool?: { type: CHALLENGE_PRIZE_TYPE, amounts: PrizePoolAmounts } | null) {
  if (!pool) return undefined

  const prize = pool.amounts.find(({ fromRank, toRank }) => position >= fromRank && position <= toRank)
  if (prize && prize.badgeId) {
    return availableBadges.find((badge) => badge.id === prize.badgeId)
  }
  return undefined
}

export function getCardMedal(rank: number): ChallengeMedal | undefined {
  switch (rank) {
  case 1:
    return ChallengeMedal.GOLD
  case 2:
    return ChallengeMedal.SILVER
  case 3:
    return ChallengeMedal.BRONZE
  default:
    return undefined
  }
}

export function useChallengeBase(challenge: Challenge | null) {
  const prizePool = computed(() => {
    const pool = challenge?.prizePool
    if (!pool) return null
    return getNormalizedPrizePool(pool)
  })

  const totalPrizes = computed(() => calculateTotalPrize(challenge?.prizePool))

  const winnersCount = computed(() => {
    if (!challenge) return 0
    return challenge.prizePool?.amounts[challenge.prizePool.amounts.length - 1]?.toRank ?? 0
  })

  const hasNftPrize = computed(() => prizePool.value?.amounts.some(isPrizePoolNft))
  const isBadgeOnly = computed(() => prizePool.value?.type === CHALLENGE_PRIZE_TYPE.BADGE)
  const isSpecialCurrencyOnly = computed(() => prizePool.value?.type === CHALLENGE_PRIZE_TYPE.SPECIAL_CURRENCY)
  const hasMultipleSpecialCurrencies = computed(() => {
    const uniqueCurrencyCount = new Set(
      prizePool.value?.amounts
        .map(amount => isPrizePoolSpecialCurrency(amount) && amount.specialCurrencyId)
        .filter((id): id is SPECIAL_CURRENCY_ID => !!id),
    ).size
    return uniqueCurrencyCount > 1
  })

  const isAllMysteryBoxes = computed(() => {
    const specialCurrencyIds = prizePool.value?.amounts
      .map(amount => isPrizePoolSpecialCurrency(amount) && amount.specialCurrencyId)
      .filter((id): id is SPECIAL_CURRENCY_ID => !!id) ?? []
    return specialCurrencyIds.length > 0 && specialCurrencyIds.every(id => id.startsWith('mysterybox.'))
  })

  const prizeTypeImage = computed(() => {
    if (hasNftPrize.value) return NftImage
    if (isSpecialCurrencyOnly.value && challenge?.prizePool?.amounts?.[0] && isPrizePoolSpecialCurrency(challenge?.prizePool?.amounts?.[0])) {
      return allSpecialCurrenciesConfig.value[challenge?.prizePool?.amounts?.[0].specialCurrencyId]?.image ?? ''
    }
    return getPrizeTypeImage(prizePool.value?.type)
  })

  const prizeTypeText = computed(() => {
    if (hasNftPrize.value) return 'TG Gifts'
    if (isBadgeOnly.value) return 'Badges'
    if (hasMultipleSpecialCurrencies.value) {
      if (isAllMysteryBoxes.value) {
        return 'Mystery Boxes'
      }
      return 'Collectibles'
    }
    if (isSpecialCurrencyOnly.value && !hasMultipleSpecialCurrencies.value) {
      return formatNumberFullTruncate(totalPrizes.value, !Number.isInteger(totalPrizes.value))
    }
    return formatNumberFullTruncate(totalPrizes.value, !Number.isInteger(totalPrizes.value))
  })

  function getPrizeForPosition(position: number) {
    return getPrize(position, challenge?.prizePool)
  }

  function getPrizeImageForPosition(position: number) {
    const prize = getPrizeForPosition(position)
    if (!prize) return
    if (isPrizePoolNft(prize)) {
      return prize.lottieUrl
    }
    if (isPrizePoolSpecialCurrency(prize)) {
      return allSpecialCurrenciesConfig.value[prize.specialCurrencyId]?.image
    }
    return prizeTypeImage.value
  }

  function getPrizeNameForPosition(position: number) {
    const prize = getPrizeForPosition(position)
    if (!prize) return
    if (isPrizePoolNft(prize)) {
      return prize.nft
    }
    return prizePool.value?.type
  }

  function getPrizeValueForPosition(position: number) {
    const prize = getPrizeForPosition(position)
    if (!prize || !(isPrizePoolCash(prize) || isPrizePoolSpecialCurrency(prize))) return 0
    return prize.amount
  }

  function getBadgeForPosition(position: number) {
    return getBadge(position, challenge?.prizePool)
  }

  return {
    winnersCount,
    prizePool,
    prizeTypeImage,
    prizeTypeText,
    totalPrizes,
    hasNftPrize,
    isBadgeOnly,
    getPrizeTypeImage,
    getPrizeForPosition,
    getPrizeNameForPosition,
    getPrizeImageForPosition,
    getPrizeValueForPosition,
    getBadgeForPosition,
    getCardMedal,
  }
}

export const useDailyUpChallenge = () => {
  const { config } = useConfig()
  const { counterToNextDay } = useNow()

  const dailyUpChallenge = computed(() => {
    const challenge = config.value.game.challenges.dailyUp
    if (!challenge || !challenge.prizePool?.amounts.length) return null
    return challenge
  })

  const baseChallenge = useChallengeBase(dailyUpChallenge.value)

  return {
    dailyUpChallenge,
    counterToNextDay,
    formattedCountdown: computed(() => formatDuration(counterToNextDay.value)),
    ...baseChallenge,
  }
}

export const useSpecialCollectibleChallenge = () => {
  const { config } = useConfig()
  const { now } = useNow()

  const specialCollectibleChallenge = computed(() => {
    const challenge = config.value.game.challenges.specialCollectible
    if (!challenge || !challenge.prizePool?.amounts.length) return null
    return challenge
  })

  const baseChallenge = useChallengeBase(specialCollectibleChallenge.value)

  const specialCollectibleChallengeStartTimestamp = computed(() => specialCollectibleChallenge.value?.prizePool?.from ?? 0)
  const specialCollectibleChallengeEndTimestamp = computed(() => specialCollectibleChallenge.value?.prizePool?.to ?? 0)
  const specialCollectibleChallengeCurrency = computed(() => specialCollectibleChallenge.value?.prizePool?.currency)
  const specialCollectibleChallengeName = computed(() => specialCollectibleChallenge.value?.prizePool.challengeName ?? 'Collectible Hunt')

  const isSpecialCollectibleChallengeLive = computed(() => now.value >= specialCollectibleChallengeStartTimestamp.value && now.value < specialCollectibleChallengeEndTimestamp.value)

  const currencyLabel = computed(() => specialCollectibleChallengeCurrency.value ? allSpecialCurrenciesConfig.value[specialCollectibleChallengeCurrency.value]?.label : undefined)
  const currencyImage = computed(() => specialCollectibleChallengeCurrency.value ? allSpecialCurrenciesConfig.value[specialCollectibleChallengeCurrency.value]?.image : undefined)

  const countdown = computed(() => {
    return Math.max(0, specialCollectibleChallengeEndTimestamp.value - now.value)
  })

  return {
    specialCollectibleChallenge,
    ...baseChallenge,
    countdown,
    specialCollectibleChallengeStartTimestamp,
    specialCollectibleChallengeEndTimestamp,
    specialCollectibleChallengeCurrency,
    specialCollectibleChallengeName,
    isSpecialCollectibleChallengeLive,
    formattedCountdown: computed(() => formatDuration(
      countdown.value,
      undefined,
      countdown.value > 3600 * 72,
    )),
    currencyLabel,
    currencyImage,
  }
}

export const usePathwayCompletionChallenge = () => {
  const { config } = useConfig()
  const { now } = useNow()

  const pathwayCompletionChallenge = computed(() => {
    const challenge = config.value.game.challenges.pathwaysCompletion
    if (!challenge || !challenge.prizePool?.amounts.length) return null
    return challenge
  })

  const baseChallenge = useChallengeBase(pathwayCompletionChallenge.value)

  const pathwayCompletionChallengeStartTimestamp = computed(() => pathwayCompletionChallenge.value?.prizePool?.from ?? 0)
  const pathwayCompletionChallengeEndTimestamp = computed(() => pathwayCompletionChallenge.value?.prizePool?.to ?? 0)
  const isPathwayCompletionChallengeLive = computed(() => now.value >= pathwayCompletionChallengeStartTimestamp.value && now.value < pathwayCompletionChallengeEndTimestamp.value)

  const countdown = computed(() => {
    return Math.max(0, pathwayCompletionChallengeEndTimestamp.value - now.value)
  })

  return {
    pathwayCompletionChallenge,
    ...baseChallenge,
    countdown,
    pathwayCompletionChallengeStartTimestamp,
    pathwayCompletionChallengeEndTimestamp,
    isPathwayCompletionChallengeLive,
    formattedCountdown: computed(() => formatDuration(
      countdown.value,
      undefined,
      countdown.value > 3600 * 72,
    )),
  }
}

export const usePuzzleChallenge = () => {
  const { config } = useConfig()
  const { now } = useNow()

  const puzzleChallenge = computed(() => {
    const challenge = config.value.game.challenges.puzzleChallenge
    if (!challenge || !challenge.prizePool?.amounts.length) return null
    return challenge
  })

  const baseChallenge = useChallengeBase(puzzleChallenge.value)

  const puzzleChallengeStartTimestamp = computed(() => puzzleChallenge.value?.prizePool?.from ?? 0)
  const puzzleChallengeEndTimestamp = computed(() => puzzleChallenge.value?.prizePool?.to ?? 0)
  const isPuzzleChallengeLive = computed(() => now.value >= puzzleChallengeStartTimestamp.value && now.value < puzzleChallengeEndTimestamp.value)

  const countdown = computed(() => {
    return Math.max(0, puzzleChallengeEndTimestamp.value - now.value)
  })

  return {
    puzzleChallenge,
    ...baseChallenge,
    countdown,
    puzzleChallengeStartTimestamp,
    puzzleChallengeEndTimestamp,
    isPuzzleChallengeLive,
    formattedCountdown: computed(() => formatDuration(
      countdown.value,
      undefined,
      countdown.value > 3600 * 72,
    )),
  }
}

export const usePuzzlesCompletionChallenge = () => {
  const { config } = useConfig()
  const { now } = useNow()

  const firstExistingPuzzle = computed(() => config.value.game.rewardLiveOps.find(rlo => rlo.challenge === REWARD_LIVE_OP_CHALLENGE_TYPE.PUZZLE))

  const puzzlesCompletionChallenge = computed(() => {
    const challenge = config.value.game.challenges.puzzlesCompletionChallenge
    if (!challenge || !challenge.prizePool?.amounts.length || !firstExistingPuzzle.value) return null
    return challenge
  })

  const baseChallenge = useChallengeBase(puzzlesCompletionChallenge.value)

  const puzzlesCompletionChallengeStartTimestamp = computed(() => puzzlesCompletionChallenge.value?.prizePool?.from ?? 0)
  const puzzlesCompletionChallengeEndTimestamp = computed(() => puzzlesCompletionChallenge.value?.prizePool?.to ?? 0)
  const isPuzzleCompletionChallengeLive = computed(() => now.value >= puzzlesCompletionChallengeStartTimestamp.value && now.value < puzzlesCompletionChallengeEndTimestamp.value)

  const countdown = computed(() => {
    return Math.max(0, puzzlesCompletionChallengeEndTimestamp.value - now.value)
  })

  return {
    puzzlesCompletionChallenge,
    ...baseChallenge,
    countdown,
    puzzlesCompletionChallengeStartTimestamp,
    puzzlesCompletionChallengeEndTimestamp,
    isPuzzleCompletionChallengeLive,
    firstExistingPuzzle,
    formattedCountdown: computed(() => formatDuration(
      countdown.value,
      undefined,
      countdown.value > 3600 * 72,
    )),
  }
}

export const useMemoryChallenge = () => {
  const { config } = useConfig()
  const { now } = useNow()

  const memoryChallenge = computed(() => {
    const challenge = config.value.game.challenges.memoryGameChallenge
    if (!challenge || !challenge.prizePool?.amounts.length) return null
    return challenge
  })

  const baseChallenge = useChallengeBase(memoryChallenge.value)

  const memoryChallengeStartTimestamp = computed(() => memoryChallenge.value?.prizePool?.from ?? 0)
  const memoryChallengeEndTimestamp = computed(() => memoryChallenge.value?.prizePool?.to ?? 0)
  const isMemoryChallengeLive = computed(() => now.value >= memoryChallengeStartTimestamp.value && now.value < memoryChallengeEndTimestamp.value)

  // Get the cost from the config, fallback to 5 gems if not specified
  const challengeCost = computed(() => memoryChallenge.value?.prizePool?.cost ?? null)
  const entryFee = computed(() => challengeCost.value?.amount ?? 0)
  const entryCurrency = computed(() => challengeCost.value?.currencyId ?? SPECIAL_CURRENCY_ID.GEM)
  const currencyImage = computed(() => allSpecialCurrenciesConfig.value[entryCurrency.value]?.image ?? '')

  const countdown = computed(() => {
    return Math.max(0, memoryChallengeEndTimestamp.value - now.value)
  })

  return {
    memoryChallenge,
    ...baseChallenge,
    countdown,
    memoryChallengeStartTimestamp,
    memoryChallengeEndTimestamp,
    isMemoryChallengeLive,
    formattedCountdown: computed(() => formatDuration(
      countdown.value,
      undefined,
      countdown.value > 3600 * 72,
    )),
    entryFee,
    entryCurrency,
    currencyImage,
    challengeCost,
  }
}

export const useMinesweeperChallenge = () => {
  const { config } = useConfig()
  const { now } = useNow()

  const minesweeperChallenge = computed(() => {
    const challenge = config.value.game.challenges.minesweeperGameChallenge
    if (!challenge || !challenge.prizePool?.amounts.length) return null
    return challenge
  })

  const baseChallenge = useChallengeBase(minesweeperChallenge.value)

  const minesweeperChallengeStartTimestamp = computed(() => minesweeperChallenge.value?.prizePool?.from ?? 0)
  const minesweeperChallengeEndTimestamp = computed(() => minesweeperChallenge.value?.prizePool?.to ?? 0)
  const isMinesweeperChallengeLive = computed(() => now.value >= minesweeperChallengeStartTimestamp.value && now.value < minesweeperChallengeEndTimestamp.value)

  // Get the cost from the config, fallback to 5 gems if not specified
  const challengeCost = computed(() => minesweeperChallenge.value?.prizePool?.cost ?? null)
  const entryFee = computed(() => challengeCost.value?.amount ?? 0)
  const entryCurrency = computed(() => challengeCost.value?.currencyId ?? SPECIAL_CURRENCY_ID.GEM)
  const currencyImage = computed(() => allSpecialCurrenciesConfig.value[entryCurrency.value]?.image ?? '')

  const countdown = computed(() => {
    return Math.max(0, minesweeperChallengeEndTimestamp.value - now.value)
  })

  return {
    minesweeperChallenge,
    ...baseChallenge,
    countdown,
    minesweeperChallengeStartTimestamp,
    minesweeperChallengeEndTimestamp,
    isMinesweeperChallengeLive,
    formattedCountdown: computed(() => formatDuration(
      countdown.value,
      undefined,
      countdown.value > 3600 * 72,
    )),
    entryFee,
    entryCurrency,
    currencyImage,
    challengeCost,
  }
}
